import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

export interface Company {
  id: string;
  name: string;
  ruc: string | null;
  country: string | null;
  plan: 'trial' | 'starter' | 'professional' | 'enterprise';
  active: boolean;
  max_warehouses: number | null;
  max_products: number | null;
  max_users: number | null;
  trial_ends_at: string | null;
  emergency_access_enabled: boolean;
  corporate_domain: string | null;
  selected_plan: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  billing_cycle: string | null;
  email_notifications: boolean;
  is_dev: boolean;
}

export interface Profile {
  id: string;
  company_id: string | null;
  full_name: string | null;
  email: string | null;
  role: 'admin' | 'manager' | 'viewer' | 'superadmin';
  active: boolean;
  terms_accepted_at: string | null;
  terms_accepted_version: string | null;
  onboarding_completed: boolean;
}

interface CompanyContextValue {
  company: Company | null;
  profile: Profile | null;
  loading: boolean;
  refresh: () => void;
}

const CompanyContext = createContext<CompanyContextValue>({
  company: null,
  profile: null,
  loading: true,
  refresh: () => {},
});

export function CompanyProvider({ user, children }: { user: User; children: React.ReactNode }) {
  const [company, setCompany] = useState<Company | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    // Query 1: fetch own profile — uses auth.uid() directly, avoids circular RLS
    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('id, company_id, full_name, email, role, active, terms_accepted_at, terms_accepted_version, onboarding_completed')
      .eq('id', user.id)
      .maybeSingle();

    if (profErr) {
      console.error('[CompanyContext] profiles query failed:', profErr.message, profErr.details);
      setProfile(null);
      setCompany(null);
      setLoading(false);
      return;
    }

    if (!prof) {
      // Profile does not exist yet for this user
      setProfile(null);
      setCompany(null);
      setLoading(false);
      return;
    }

    setProfile(prof as Profile);

    // Query 2: fetch company — explicit eq on the id we already know
    // If this fails (RLS not set up yet), profile is still available for the rest of the app
    const { data: co, error: coErr } = await supabase
      .from('companies')
      .select('id, name, ruc, country, plan, active, max_warehouses, max_products, max_users, trial_ends_at, emergency_access_enabled, corporate_domain, selected_plan, subscription_status, current_period_end, billing_cycle, email_notifications, is_dev')
      .eq('id', prof.company_id)
      .maybeSingle();

    if (coErr) {
      console.error(
        '[CompanyContext] companies query failed — check Supabase RLS for companies table.',
        '\nError:', coErr.message,
        '\nFix: run the SQL below in Supabase → SQL Editor:',
        `\n  DROP POLICY IF EXISTS "co_select" ON public.companies;`,
        `\n  CREATE POLICY "co_select" ON public.companies`,
        `\n    FOR SELECT USING (`,
        `\n      id IN (SELECT company_id FROM public.profiles WHERE profiles.id = auth.uid())`,
        `\n    );`,
      );
    }

    setCompany((co as Company) ?? null);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <CompanyContext.Provider value={{ company, profile, loading, refresh: load }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);

export const PLAN_LABELS: Record<string, string> = {
  trial:        'Trial',
  starter:      'Starter',
  professional: 'Professional',
  enterprise:   'Enterprise',
};

export const PLAN_COLORS: Record<string, string> = {
  trial:        '#f59e0b',
  starter:      '#64748b',
  professional: '#3b82f6',
  enterprise:   '#a78bfa',
};

export const ROLE_LABELS: Record<string, string> = {
  admin:      'Administrador',
  manager:    'Gestor',
  viewer:     'Visualizador',
  superadmin: 'Super Admin',
};

export const ROLE_COLORS: Record<string, { color: string; bg: string }> = {
  admin:      { color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  manager:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.15)'  },
  viewer:     { color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
  superadmin: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
};
